import { Check } from "@mui/icons-material";
import { Box, Step, StepButton, StepButtonProps, StepIndicator, Stepper } from "@mui/joy";
import { createLink } from "@tanstack/react-router";
import { ApiChallenge } from "griptonite-api-client";
import { useMemo } from "react";

import Warning from "@/assets/icons/Warning";

import getPublishWarnings, { useHideWarningsMap } from "./getPublishWarnings";

const steps = [
  {
    label: "Challenge Details",
    name: "details",
  },
  {
    label: "Rules and Rewards",
    name: "rules",
  },
  {
    label: "Promotion",
    name: "promotion",
  },
] as const;

const StepButtonA = (props: StepButtonProps) => <StepButton component="a" {...props} />;
const StepButtonLink = createLink(StepButtonA);

const ChallengeStepper = ({
  challenge,
  activeStep,
}: {
  challenge?: ApiChallenge;
  activeStep: (typeof steps)[number]["name"];
}) => {
  const publishWarnings = useMemo(() => getPublishWarnings(challenge), [challenge]);

  const hidePublishWarningsMap = useHideWarningsMap(challenge?.uuid);

  return (
    <Box
      sx={{
        padding: { xs: 1.5, sm: 2 },
        borderBottom: "1px solid",
        borderColor: "neutral.200",
        backgroundColor: "neutral.50",
      }}
    >
      <Stepper sx={{ width: "100%", "--StepIndicator-size": "36px" }}>
        {steps.map((step, index) => {
          const isActive = step.name === activeStep;
          const showWarning =
            !hidePublishWarningsMap[step.name] && publishWarnings.byTab[step.name];
          const isCompleted =
            challenge &&
            !hidePublishWarningsMap[step.name] &&
            (publishWarnings.byTab[step.name] || []).length === 0;
          let variant: "outlined" | "soft" | "solid" = "outlined";
          let color: "neutral" | "primary" | "success" | "warning" = "neutral";
          let content: React.ReactNode = index + 1;
          if (isActive) {
            variant = "solid";
            color = "primary";
          } else if (isCompleted) {
            variant = "soft";
            color = "success";
            content = <Check />;
          } else if (showWarning) {
            variant = "soft";
            color = "warning";
            content = <Warning />;
          }

          return (
            <Step
              key={index}
              completed={isCompleted && !showWarning}
              disabled={!challenge}
              active={isActive}
              orientation="horizontal"
              indicator={
                <StepIndicator variant={variant} color={color}>
                  {content}
                </StepIndicator>
              }
            >
              <StepButtonLink
                to={`./${step.name}`}
                from="/gym/$venueUuid/challenges/$challengeUuid/profile"
                disabled={!challenge}
                sx={{
                  color: "neutral.900",
                }}
              >
                {step.label}
              </StepButtonLink>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default ChallengeStepper;
